import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { ApiError } from "@/api/client";
import { knowledgeBaseApi, type KbCategoryAccessRow } from "@/api/knowledgeBase";
import { rolesLookupApi } from "@/api/lookups";

// Knowledge Base v1.0 PR 4 — folder-access matrix grid. Categories on rows,
// positions on columns. An empty row = "Open to all". Checking one or more
// boxes restricts the folder to those positions. Toggling a previously-open
// folder to restricted requires confirmation.

const QUERY_KEY = ["kb", "admin", "category-access"] as const;

export function KnowledgeBaseAccessMatrixPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("KnowledgeBase.ManageDocuments");
  const toast = useToast();
  const qc = useQueryClient();

  const matrixQ = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => knowledgeBaseApi.getCategoryAccessMatrix(),
    enabled: canManage,
  });

  const rolesQ = useQuery({
    queryKey: ["roles"],
    queryFn: () => rolesLookupApi.list(),
    enabled: canManage,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { categoryCode: string; roleCodes: string[] }) =>
      knowledgeBaseApi.setCategoryAccess(vars.categoryCode, vars.roleCodes),
    onSuccess: (_d, vars) => {
      toast.success(
        vars.roleCodes.length === 0
          ? "Folder is now open to all positions."
          : `Restricted to ${vars.roleCodes.length} position${vars.roleCodes.length === 1 ? "" : "s"}.`,
      );
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? err.errors?.[0] ?? err.message : "Save failed.",
      );
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const rolesByCode = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    (rolesQ.data ?? []).forEach((r) =>
      m.set(r.code, { code: r.code, name: r.displayName }),
    );
    return m;
  }, [rolesQ.data]);

  if (!canManage) {
    return (
      <div className="text-small text-status-error">
        You do not have permission to manage knowledge base access.
      </div>
    );
  }

  const positions = rolesQ.data ?? [];
  const rows = matrixQ.data ?? [];

  const handleToggle = (row: KbCategoryAccessRow, roleCode: string) => {
    const checked = row.roleCodes.includes(roleCode);
    const wasOpen = row.roleCodes.length === 0;
    let nextCodes: string[];
    if (checked) {
      nextCodes = row.roleCodes.filter((c) => c !== roleCode);
    } else {
      if (wasOpen) {
        const ok = window.confirm(
          `'${row.categoryName}' is currently open to all positions. ` +
            `Restricting it to ${rolesByCode.get(roleCode)?.name ?? roleCode} ` +
            `will hide it from every other position. Continue?`,
        );
        if (!ok) return;
      }
      nextCodes = [...row.roleCodes, roleCode];
    }
    updateMutation.mutate({ categoryCode: row.categoryCode, roleCodes: nextCodes });
  };

  const handleReopen = (row: KbCategoryAccessRow) => {
    if (row.roleCodes.length === 0) return;
    const ok = window.confirm(
      `Re-open '${row.categoryName}' to all positions? Every authenticated user will see its documents.`,
    );
    if (!ok) return;
    updateMutation.mutate({ categoryCode: row.categoryCode, roleCodes: [] });
  };

  const loading = matrixQ.isLoading || rolesQ.isLoading;

  return (
    <>
      <PageHeader
        eyebrow="Admin · Knowledge Base"
        title="Folder access"
        description="Restrict knowledge base folders to specific positions. Folders with no positions checked are open to every authenticated user."
      />

      <div
        role="note"
        className="flex items-start gap-space-2 border border-border-light bg-white-warm rounded-md p-space-3 mb-space-4"
      >
        <Info size={16} aria-hidden className="text-gold-shadow mt-[2px]" />
        <p className="text-caption text-gray-dark m-0">
          <strong>Empty row = open to everyone.</strong> Checking one or more
          positions restricts the folder to those positions only. Changes save
          immediately.
        </p>
      </div>

      {loading ? (
        <div className="text-caption text-gray-mid uppercase tracking-wide py-space-6 text-center">
          Loading matrix…
        </div>
      ) : positions.length === 0 ? (
        <div className="text-small text-gray-mid italic">No positions defined.</div>
      ) : rows.length === 0 ? (
        <div className="text-small text-gray-mid italic">
          No categories defined yet. Create a category first.
        </div>
      ) : (
        <div className="overflow-x-auto border border-border-light rounded-md bg-white-pure">
          <table className="w-full text-small">
            <thead>
              <tr className="bg-white-warm">
                <th className="text-left px-space-3 py-space-2 font-display text-label uppercase tracking-label text-gray-mid sticky left-0 bg-white-warm">
                  Category
                </th>
                {positions.map((p) => (
                  <th
                    key={p.code}
                    className="text-center px-space-2 py-space-2 font-display text-label uppercase tracking-label text-gray-mid"
                    title={p.displayName}
                  >
                    <div className="font-mono text-onyx font-bold">{p.code}</div>
                    <div className="text-[9px] text-gray-mid font-normal normal-case tracking-normal mt-[1px]">
                      {p.displayName}
                    </div>
                  </th>
                ))}
                <th className="text-right px-space-3 py-space-2 font-display text-label uppercase tracking-label text-gray-mid">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = row.roleCodes.length === 0;
                return (
                  <tr key={row.categoryCode} className="border-t border-border-light">
                    <td className="px-space-3 py-space-2 sticky left-0 bg-white-pure">
                      <div className="font-semibold text-onyx">{row.categoryName}</div>
                      <div
                        className={
                          isOpen
                            ? "text-caption text-gold-shadow italic"
                            : "text-caption text-status-warning italic"
                        }
                      >
                        {isOpen
                          ? "Open to all"
                          : `Restricted (${row.roleCodes.length})`}
                      </div>
                    </td>
                    {positions.map((p) => {
                      const checked = row.roleCodes.includes(p.code);
                      return (
                        <td
                          key={p.code}
                          className="text-center px-space-2 py-space-2"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={updateMutation.isPending}
                            onChange={() => handleToggle(row, p.code)}
                            aria-label={`Grant ${p.code} access to ${row.categoryName}`}
                            className="h-4 w-4 accent-[var(--gold)]"
                          />
                        </td>
                      );
                    })}
                    <td className="text-right px-space-3 py-space-2">
                      {!isOpen && (
                        <button
                          type="button"
                          onClick={() => handleReopen(row)}
                          disabled={updateMutation.isPending}
                          className="text-caption font-display font-semibold text-gold-shadow hover:underline disabled:opacity-50"
                        >
                          Re-open
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
